import React, { useEffect, useMemo, useState } from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { useCountries } from "services/countriesService";

export const filterCountries = (countries, term) => {
  const search = (term || "").toLowerCase().trim();
  if (!search) return countries;

  return countries.filter((item) => {
    const name = (item.countryName || "").toLowerCase();
    const code = (item.countryCode || "").toLowerCase();
    const official = (item.officialName || "").toLowerCase();
    return (
      name.includes(search) ||
      code.includes(search) ||
      official.includes(search)
    );
  });
};

export const findCountryMatch = (countries, candidate) => {
  if (!candidate) return null;

  const name =
    typeof candidate === "object"
      ? candidate.countryName
      : String(candidate).trim();
  const code =
    typeof candidate === "object" ? candidate.countryCode : String(candidate).trim();

  return (
    countries.find(
      (c) =>
        (name && c.countryName === name) ||
        (code && c.countryCode === code),
    ) ?? null
  );
};

export const resolveCountryValue = (value) => {
  if (!value) return null;

  if (typeof value === "object") {
    const name = (value.countryName || "").trim();
    const code = (value.countryCode || "").trim();
    if (!name && !code) return null;
    return {
      ...value,
      countryName: name || code,
      countryCode: code || name,
    };
  }

  const str = String(value).trim();
  return str ? { countryName: str, countryCode: str } : null;
};

export const withSelectedCountry = (countries, value) => {
  const resolved = resolveCountryValue(value);
  if (!resolved || findCountryMatch(countries, resolved)) {
    return countries;
  }
  return [resolved, ...countries];
};

export const CountryComboBox = ({
  value,
  onChange,
  disabled,
  loading: loadingProp,
  placeholder = "Search Country",
  ...others
}) => {
  const { data: countries, loading: countriesLoading } = useCountries();
  const loading = loadingProp ?? countriesLoading;
  const countriesWithSelected = useMemo(
    () => withSelectedCountry(countries, value),
    [countries, value],
  );
  const [filtered, setFiltered] = useState(countriesWithSelected);

  useEffect(() => {
    setFiltered(countriesWithSelected);
  }, [countriesWithSelected]);

  const handleFilterChange = (e) => {
    setFiltered(filterCountries(countriesWithSelected, e.filter?.value));
  };

  const selected = useMemo(() => {
    const match = findCountryMatch(countriesWithSelected, value);
    if (match) return match;
    if (value && typeof value === "object") return resolveCountryValue(value);
    return null;
  }, [countriesWithSelected, value]);

  return (
    <ComboBox
      {...others}
      disabled={disabled}
      loading={loading}
      data={filtered}
      textField="countryName"
      dataItemKey="countryCode"
      filterable={true}
      iconClassName="wa"
      clearButton={true}
      placeholder={placeholder}
      value={selected}
      onChange={(e) => {
        const nextValue = e.value;
        if (typeof value === "object") {
          onChange?.(nextValue ?? null);
          return;
        }
        onChange?.(nextValue?.countryName ?? "");
      }}
      onFilterChange={handleFilterChange}
    />
  );
};

const CountryComboField = (fieldRenderProps) => {
  const { value, onChange, disabled, ...others } = fieldRenderProps;

  return (
    <CountryComboBox
      {...others}
      value={value}
      disabled={disabled}
      onChange={(countryName) => onChange({ value: countryName })}
    />
  );
};

export default CountryComboField;
